<template>
  <b-global-loader :loading="$loading.isLoading('tryAutoReadPrivateKey')">
    <v-row align="center">
      <v-col v-if="keyType === 'file'" cols="12">
        <div class="label" style="font-size: 11px !important">
          Кваліфікований надавач електронних довірчих послуг
        </div>
        <b-select
          v-model="privateKeyData.caAddress"
          :item-title="ca => ca.issuerCNs[0]"
          :items="[
            { issuerCNs: ['Визначати автоматично'], address: 'auto' },
            ...caList,
          ]"
          hide-details
          placeholder="Оберіть зі списку"
          item-value="address"></b-select>
      </v-col>
      <v-col v-if="keyType === 'media'" cols="12">
        <b-select
          v-model="privateKeyData.privateKey"
          class="mb-3"
          :error-messages="getErrorMessages(v$.privateKey)"
          :items="keyMedias"
          :return-object="true"
          item-title="visibleName"
          label="Носій ос. ключа"
          placeholder="Оберіть носій ос. ключа"
          hide-details
          @blur="v$.privateKey.$touch()"></b-select>
        <v-card
          style="border: 2px dashed rgb(197, 197, 208); padding: 32px 20px"
          class="text-center cursor-pointer">
          <v-icon
            icon="intime:key-media"
            size="44"
            color="grey-lighten-1"></v-icon>
          <div class="font-size-16 font-weight-bold mb-2">
            Підключіть токен до USB-порту
          </div>
          <div class="text-disabled font-size-13">
            Програма автоматично виявить пристрій
          </div>
        </v-card>
      </v-col>
      <v-col v-if="keyType === 'file'" cols="12">
        <v-hover v-slot="{ isHovering, props }">
          <v-card
            v-bind="props"
            variant="flat"
            style="
              border: 2px dashed rgb(197, 197, 208);
              padding: 32px 20px;
              background: rgb(250, 251, 252);
            "
            :style="{ borderColor: isHovering ? 'rgb(24, 95, 165)' : null }"
            class="text-center cursor-pointer"
            @click="selectPrivateKey">
            <div v-if="!privateKeyData.privateKey?.name">
              <div class="mb-2">
                <span class="font-size-16 font-weight-bold">
                  Перетягніть сюди файл ключа або
                </span>
                <span class="font-size-16 link"
                  >завантажте його зі свого носія</span
                >
              </div>
              <div class="text-disabled font-size-13">
                (зазвичай його назва "Key-6.dat" або *.pfx, *.pk8, *.zs2, *.jks)
              </div>
            </div>
            <div v-else>
              <div class="d-flex align-center ga-3 mb-3 justify-center">
                <v-icon size="small" icon="mdi-check" color="success"></v-icon>
                <span class="text-success font-weight-bold">
                  Ключ завантажено
                </span>
              </div>

              <div class="text-disabled text-center">
                натисніть «Видалити ключ» зверху щоб завантажити інший
              </div>
            </div>
          </v-card>
        </v-hover>
      </v-col>
    </v-row>
    <v-row align="center">
      <v-col cols="9">
        <b-input-password
          v-model="privateKeyData.password"
          :error-messages="getErrorMessages(v$.password)"
          :readonly="!!keyInfo"
          label="Пароль"
          @blur="v$.password.$touch()"></b-input-password>
      </v-col>
      <v-col cols="3">
        <v-btn
          v-if="!!keyInfo"
          class="w-100"
          color="error"
          @click="clearSignature">
          Вилучити
        </v-btn>
        <v-btn
          v-else
          variant="outlined"
          style="border-width: 2px"
          :loading="$loading.isLoading(`readPrivateKey__${id}`)"
          class="w-100"
          @click="readPrivateKey">
          Зчитати
        </v-btn>
      </v-col>
    </v-row>

    <div v-if="!stamp && keyType === 'file' && isAdmin" class="mt-5">
      <v-checkbox
        v-model="isRememberKey"
        hide-details
        style="margin-left: -10px">
        <template #label>
          <span> Запам'ятовувати ключі в цьому браузері </span>
        </template>
      </v-checkbox>
    </div>

    <div v-if="keyInfo" class="mt-2">
      <key-info :keyInfo="keyInfo"></key-info>
    </div>

    <div v-if="typeof isValidSignature === 'boolean'" class="mt-6">
      <span v-if="isValidSignature" class="d-flex ga-3 align-center">
        <v-icon icon="mdi-check" size="small" color="success"></v-icon>
        <span class="text-success"
          >{{ stamp ? 'Печатку' : 'Ключ' }} успішно зчитано</span
        >
      </span>
      <span v-else class="text-error">
        {{ error }}
      </span>
    </div>
  </b-global-loader>
</template>

<script lang="ts">
import { caList } from '@/utils/signature/caList'
import {
  BGlobalLoader,
  BInputPassword,
  selectFile,
} from 'best-modules/components'
import KeyInfo from './KeyInfo.vue'
import { generateId, getErrorMessages } from 'best-modules/utils'
import { required, useVuelidate } from '@/plugins/vuelidate.js'
import { computed, onBeforeUnmount, PropType, reactive, ref, watch } from 'vue'
import { handleAsync } from 'best-modules/plugins'
import {
  keyMedias,
  readPrivateKeyFile,
  readPrivateKeyMedia,
  removeKey,
  tryAutoReadPrivateKey,
} from '@/utils/signature'
import {
  clearRememberedKey,
  hasRememberedKey,
} from '@/utils/signature/rememberKey'
import { KeyType } from '../types'
import { useStore } from 'vuex'
import { notifyValidation } from '@/utils/helpers'

export default {
  name: 'KeyReader',
  components: { KeyInfo, BInputPassword, BGlobalLoader },
  props: {
    keyType: {
      type: String as PropType<KeyType>,
    },
    stamp: {
      type: Boolean as PropType<boolean>,
    },
  },
  data: () => {
    return {
      caList,
    }
  },
  methods: { getErrorMessages },
  setup(props) {
    const store = useStore()
    const id: string = generateId()
    const keyInfo = ref<any>(null)
    const isValidSignature = ref<boolean | null>(null)
    const error = ref<any>(null)

    const isAdmin = computed(() => {
      return store.state.user.userData?.isAdmin
    })

    const isRememberKey = ref(
      hasRememberedKey() &&
        !props.stamp &&
        props.keyType === 'file' &&
        isAdmin.value
    )

    if (isRememberKey.value) {
      handleAsync('tryAutoReadPrivateKey', () => {
        return tryAutoReadPrivateKey(id).then(result => {
          if (result) {
            const { saved, key } = result
            privateKeyData.password = saved.password
            privateKeyData.privateKey = new Blob([saved.keyBytes])
            privateKeyData.caAddress = saved.meta.caCN || 'auto'
            isValidSignature.value = true
            keyInfo.value = key.ownerInfo
          } else {
            isRememberKey.value = false
          }
        })
      })
    }

    watch(isRememberKey, v => {
      if (!v) {
        clearRememberedKey()
      }
    })

    const privateKeyData = reactive({
      privateKey: null as any,
      password: null as any,
      caAddress: 'auto',
    })

    const rules = {
      privateKey: { required },
      password: { required },
    }
    const v$ = useVuelidate(rules, privateKeyData)

    const selectPrivateKey = async () => {
      const { blob } = await selectFile({
        accept: ['.dat', '.zs2', '.sk', '.jks', '.pk8', '.pfx'],
        multiple: false,
      })

      privateKeyData.privateKey = blob[0]
    }

    const clearSignature = () => {
      privateKeyData.privateKey = null
      isValidSignature.value = null
      privateKeyData.password = null
      keyInfo.value = null
      removeKey(id)
    }

    const readPrivateKey = () => {
      v$.value.$validate()
      if (!v$.value.$invalid) {
        return handleAsync(`readPrivateKey__${id}`, async () => {
          const read =
            props.keyType === 'media' ? readPrivateKeyMedia : readPrivateKeyFile

          console.log('privateKey', privateKeyData.privateKey)

          try {
            const { ownerInfo } = await read(
              id,
              privateKeyData.privateKey,
              privateKeyData.password,
              privateKeyData.caAddress,
              isRememberKey.value
            )
            if (
              props.stamp &&
              !['Цифрова печатка', '1.2.804.2.1.1.1.3.9'].includes(
                ownerInfo.extKeyUsages
              )
            ) {
              removeKey(id)
              throw new Error(
                'Ви завантажили ключ підпису. Будь ласка, оберіть ключ печатки.'
              )
            }
            keyInfo.value = ownerInfo
            isValidSignature.value = true
            error.value
          } catch (e) {
            console.log(e)
            error.value = e?.message || e
            isValidSignature.value = false
          }
        })
      } else {
        notifyValidation(v$.value)
      }
    }

    onBeforeUnmount(() => {
      removeKey(id)
    })

    return {
      privateKeyData,
      v$,
      readPrivateKey,
      isValidSignature,
      error,
      keyInfo,
      selectPrivateKey,
      clearSignature,
      id,
      keyMedias,
      isRememberKey,
      isAdmin,
    }
  },
}
</script>

<style lang="scss" scoped></style>
